import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem("wbt_user");
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });
  const [permissions, setPermissions] = useState(null); // {permissions:{...}, allowed_statuses:[], name}
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    try {
      const r = await api.get("/users/me/permissions");
      setPermissions(r.data);
    } catch {
      setPermissions(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("wbt_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        localStorage.setItem("wbt_user", JSON.stringify(r.data));
        await loadPermissions();
      })
      .catch(() => {
        localStorage.removeItem("wbt_token");
        localStorage.removeItem("wbt_user");
        setUser(null);
        setPermissions(null);
      })
      .finally(() => setLoading(false));
  }, [loadPermissions]);

  const login = async (username, password) => {
    const r = await api.post("/auth/login", { username, password });
    localStorage.setItem("wbt_token", r.data.token);
    localStorage.setItem("wbt_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    await loadPermissions();
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("wbt_token");
    localStorage.removeItem("wbt_user");
    setUser(null);
    setPermissions(null);
  };

  const hasPerm = useCallback(
    (key) => {
      if (!user) return false;
      if (user.role === "admin") return true; // mirror backend safeguard
      return !!(permissions?.permissions || {})[key];
    },
    [user, permissions]
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, permissions, hasPerm, reloadPermissions: loadPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
